// SVG-driven human body with activation heat-map. Front + back views.
//
// The base SVGs come from a Figma export — see scripts/build-muscle-map.ts.
// muscle-map.json maps each muscle_id to the path indices in front.svg /
// back.svg that belong to it. The component fetches the SVG text, injects
// it via dangerouslySetInnerHTML, then walks paths by index applying fills
// based on activation intensity.

import { useEffect, useMemo, useRef, useState } from 'react';
import muscleMapJson from '../assets/diagram/muscle-map.json';
import css from './BodyDiagram.module.css';

export interface MuscleRow {
  muscle_id: string;
  name: string;
  sets_primary: number;
  sets_secondary: number;
  sets_total: number;
  tonnage_current: number;
  tonnage_peak: number;
  ratio: number;
}

interface Props {
  // Window is controlled by the parent (Dashboard) so it stays in sync
  // with the region-balance panel beside it.
  days: number;
  fetchFor: (days: number) => Promise<{ muscles: MuscleRow[] } | null>;
}

type View = 'front' | 'back';

const MUSCLE_INDICES = muscleMapJson.muscleIndices as Record<View, Record<string, number[]>>;
const FRONT_URL = '/assets/diagram/front.svg';
const BACK_URL = '/assets/diagram/back.svg';
// Only paths whose original fill matches MUSCLE_FILL (#BDBDBD) are "muscle"
// paths we can recolour. Skin (#E0E0E0), stroke-only outlines, and the body
// silhouette stroke (path 121 in front, similar in back) are left alone —
// filling those would cover every muscle underneath.
const MUSCLE_FILL = '#BDBDBD';
// Intensity ramp — 0 = no activity (original muscle gray), 4 = peak (bright
// red). lvl0 matches the source fill so unhighlighted muscles look exactly
// like the Figma export. Bright end (#ef4444) matches the by-exercise
// primary-muscle highlight in the MuscleMapInspector, so the colour
// semantics stay consistent across the app.
const INTENSITY_FILLS = [MUSCLE_FILL, '#3a1a1a', '#7c2222', '#b91c1c', '#ef4444'];

export function BodyDiagram({ days, fetchFor }: Props) {
  const [data, setData] = useState<MuscleRow[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    fetchFor(days).then((r) => {
      if (!cancelled) setData(r?.muscles ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, [days, fetchFor]);

  return <BodyDiagramStatic muscles={data ?? []} showBreakdown />;
}

// Stateless variant — takes a pre-fetched muscle list and just renders.
// Used by the per-session detail view (no window picker, no fetch).
export function BodyDiagramStatic({
  muscles,
  showBreakdown,
  compact,
}: {
  muscles: MuscleRow[];
  showBreakdown?: boolean;
  compact?: boolean;
}) {
  const byMuscle = useMemo(() => {
    const m = new Map<string, MuscleRow>();
    for (const r of muscles) m.set(r.muscle_id, r);
    return m;
  }, [muscles]);

  // Intensity is per-muscle: current tonnage ÷ that muscle's personal peak.
  // So a muscle showing 80% means "I'm hitting it at 80% of how hard I've
  // ever hit it", regardless of whether it's a quad or a bicep.
  function intensity(id: string): number {
    const r = byMuscle.get(id);
    if (!r) return 0;
    const ratio = r.ratio;
    if (ratio === 0) return 0;
    if (ratio < 0.2) return 1;
    if (ratio < 0.45) return 2;
    if (ratio < 0.75) return 3;
    return 4;
  }

  return (
    <div>
      <div className={`${css.svgRow}${compact ? ` ${css.svgRowCompact}` : ''}`}>
        <BodyView view="front" intensity={intensity} byMuscle={byMuscle} />
        <BodyView view="back" intensity={intensity} byMuscle={byMuscle} />
      </div>

      <div className={css.legend}>
        <span className={css.legendLabel}>vs your peak</span>
        {INTENSITY_FILLS.map((c, i) => (
          <span
            key={i}
            className={css.legendCell}
            style={{ background: c }}
          />
        ))}
        <span className={css.legendLabel}>100%</span>
      </div>

      {showBreakdown && muscles.length > 0 && (
        <details className={css.details}>
          <summary>full muscle breakdown</summary>
          <ul className={css.breakdown}>
            {[...muscles]
              .sort((a, b) => b.ratio - a.ratio)
              .map((r) => (
                <li key={r.muscle_id}>
                  <span className={css.bdName}>{r.name}</span>
                  <span className={css.bdNum}>
                    {Math.round(r.ratio * 100)}% · {r.tonnage_current.toLocaleString()} kg of {r.tonnage_peak.toLocaleString()} kg peak · {r.sets_total} sets
                  </span>
                </li>
              ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function BodyView({
  view,
  intensity,
  byMuscle,
}: {
  view: View;
  intensity: (id: string) => number;
  byMuscle: Map<string, MuscleRow>;
}) {
  const [svgText, setSvgText] = useState<string | null>(null);
  const hostRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    fetch(view === 'front' ? FRONT_URL : BACK_URL)
      .then((r) => r.text())
      .then((t) => {
        if (!cancelled) setSvgText(t);
      })
      .catch(() => {
        if (!cancelled) setSvgText(null);
      });
    return () => {
      cancelled = true;
    };
  }, [view]);

  // Stable inner-HTML object so React only re-applies innerHTML when
  // svgText changes (otherwise the per-render `{__html}` literal would
  // wipe the data-* attributes we add in the classification effect).
  const innerHtmlObj = useMemo(
    () => (svgText ? { __html: svgText } : undefined),
    [svgText],
  );

  // Classify paths + tag each with its full-document index. Only paths
  // whose ORIGINAL fill is exactly MUSCLE_FILL get tagged as muscle —
  // everything else (skin layer, stroke-only body outlines) is left exactly
  // as the Figma export, since filling those covers everything underneath.
  useEffect(() => {
    const host = hostRef.current;
    if (!host || !svgText) return;
    const paths = host.querySelectorAll<SVGPathElement>('path');
    paths.forEach((p, idx) => {
      p.dataset.pathIndex = String(idx);
      const orig = (p.getAttribute('fill') ?? '').toUpperCase();
      if (orig === MUSCLE_FILL.toUpperCase()) {
        p.dataset.kind = 'muscle';
      } else {
        p.dataset.kind = 'other';
      }
    });
  }, [svgText]);

  // Apply intensity-based fills + tooltips whenever activation data changes.
  useEffect(() => {
    const host = hostRef.current;
    if (!host || !svgText) return;
    const paths = host.querySelectorAll<SVGPathElement>('path[data-kind="muscle"]');

    // First: compute a per-index target fill by walking the muscle map.
    // Later muscles will overwrite earlier ones if the same path index
    // belongs to multiple muscles (rare, but happens for overlap regions).
    const indexToFill = new Map<number, { fill: string; muscleId: string; row: MuscleRow | null }>();
    const muscleIndices = MUSCLE_INDICES[view] ?? {};
    for (const [muscleId, indices] of Object.entries(muscleIndices)) {
      const lvl = intensity(muscleId);
      const fill = INTENSITY_FILLS[lvl] ?? INTENSITY_FILLS[0]!;
      const row = byMuscle.get(muscleId) ?? null;
      // Prefer higher-intensity assignments on overlapping paths.
      for (const idx of indices) {
        const existing = indexToFill.get(idx);
        if (!existing || lvl > intensity(existing.muscleId)) {
          indexToFill.set(idx, { fill, muscleId, row });
        }
      }
    }

    // Now apply, using the full-document index stored on each path.
    paths.forEach((p) => {
      const idxStr = p.dataset.pathIndex;
      if (idxStr == null) return;
      const idx = Number(idxStr);
      const t = indexToFill.get(idx);
      const fill = t?.fill ?? INTENSITY_FILLS[0]!;
      p.setAttribute('fill', fill);

      // Tooltip
      const prev = p.querySelector('title');
      if (prev) prev.remove();
      if (t?.row) {
        const title = document.createElementNS('http://www.w3.org/2000/svg', 'title');
        title.textContent =
          `${t.row.name} — ${Math.round(t.row.ratio * 100)}% of peak ` +
          `· ${t.row.tonnage_current.toLocaleString()} kg / ${t.row.tonnage_peak.toLocaleString()} kg ` +
          `· ${t.row.sets_total} sets`;
        p.appendChild(title);
      }
    });
  }, [svgText, byMuscle, intensity, view]);

  return (
    <div className={css.view}>
      <div className={css.viewLabel}>{view}</div>
      <div ref={hostRef} className={css.svgHost} dangerouslySetInnerHTML={innerHtmlObj} />
    </div>
  );
}
